import Index from "./jsx/index";
import RouterVigilantes from "./jsx/router_vigilantes";
import RouterConsejo from "./jsx/router_consejo";
import RouterResidentes from "./jsx/router_residentes";
import React, { lazy, Suspense, useEffect } from "react";
import ProtectedRoute from "jsx/components/protectedRoute";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { checkAutoLogin } from "./services/AuthService";
import { checkPermissions } from "./services/PermisosService";
import { checkVersionAndLogout } from "./services/VersionService";
import {
  isAuthenticated,
  getTipoUsuario,
  getUserId,
} from "./store/selectors/AuthSelectors";
// import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";
import { CustomToastContainer } from "./jsx/components/CitoPlus/components/CitoPlusToastr";
import LogRocket from "logrocket";
import ReactGA from "react-ga4";
import * as Sentry from "@sentry/react";

if (process.env.NODE_ENV === "production") {
  ReactGA.initialize("G-LWJ8WBF1SJ");
  LogRocket.init("yozrd2/citoplus-v2");
}

const SignUp = lazy(() => import("./jsx/pages/Registration"));
const ForgotPassword = lazy(() => import("./jsx/pages/ForgotPassword"));
const PreRegistroVisitante = lazy(
  () => import("./jsx/pages/public/PreRegistroVisitante"),
);
const Login = lazy(() => import("./jsx/pages/Login"));

const withRouter = (Component) => (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  return <Component {...props} router={{ location, navigate, params }} />;
};

const CommonSuspense = ({ children }) => (
  <Suspense
    fallback={
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    }
  >
    {children}
  </Suspense>
);

const App = ({
  isAuthenticated,
  tipoUsuario,
  userId,
  userEmail,
  userUsername,
  router,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showLoading = useSelector((state) => state.auth.showLoading);

  useEffect(() => {
    // Restaurar auth INMEDIATAMENTE (síncrono) antes del check de versión (async).
    // Sin esto, checkAutoLogin esperaba al fetch de version.json causando un
    // delay donde el store quedaba vacío y los datos del residente no aparecían.
    checkAutoLogin(dispatch, navigate);
    checkPermissions(dispatch, navigate);

    // Verificar versión en paralelo. Si cambió, hará logout automático.
    // Se envuelve en Promise.resolve() para que sea seguro si el mock o la
    // función devuelven undefined en lugar de una Promise.
    Promise.resolve(checkVersionAndLogout(dispatch, navigate)).catch(
      (error) => {
        console.error("Error during version check flow:", error);
      },
    );
    // eslint-disable-next-line
  }, []);

  if (isAuthenticated) {
    ReactGA.set({ userId: userId });
    ReactGA.set({ user_id: userId });
    LogRocket.identify(userUsername);

    LogRocket.identify(userId, {
      name: userUsername,
      email: userEmail,
    });
  }

  const routePublic = (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/page-register" element={<SignUp />} />
      <Route path="/page-forgot-password" element={<ForgotPassword />} />
      <Route path="/ingreso/residentes/" element={<PreRegistroVisitante />} />
      <Route path="*" element={<Login />} />
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        }
      />
    </Routes>
  );

  const routePrivado = (
    <ProtectedRoute>
      {(() => {
        switch (tipoUsuario) {
          case "VIGILANTE":
            return <RouterVigilantes />;
          case "ADMIN":
            return <Index />;
          case "USUARIO":
            return <RouterConsejo />;
          case "CONSEJO":
            return <RouterConsejo />;
          case "RESIDENTE":
            return <RouterResidentes />;
          default:
            return <RouterConsejo />;
        }
      })()}
    </ProtectedRoute>
  );

  return (
    <div className={`vh-100 ${isAuthenticated ? "authenticated" : ""}`}>
      <CommonSuspense>
        {isAuthenticated ? routePrivado : routePublic}
      </CommonSuspense>
      <CustomToastContainer />
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
  tipoUsuario: getTipoUsuario(state),
  userId: getUserId(state),
  userEmail: state.auth.auth.user.email,
  userUsername: state.auth.auth.user.username,
});

const ConnectedApp = connect(mapStateToProps)(withRouter(App));

export default ConnectedApp;
